import {
  buildHanziWord,
  hanziFromHanziWord,
  meaningKeyFromHanziWord,
} from "@/dictionary";
import type { HanziWord } from "./model";

export interface PylymarkHanziWordNode {
  type: `hanziWord`;
  hanziWord: HanziWord;
  showGloss: boolean;
}

export interface PylymarkTokenNode {
  type: `token`;
  ref: string;
  text: string;
}

export interface PylymarkTextNode {
  type: `text`;
  text: string;
}

export interface PylymarkBoldNode {
  type: `bold`;
  text: string;
}

export interface PylymarkItalicNode {
  type: `italic`;
  text: string;
}

export interface PylymarkMarkNode {
  type: `mark`;
  text: string;
}

export type PylymarkNode =
  | PylymarkHanziWordNode
  | PylymarkTokenNode
  | PylymarkTextNode
  | PylymarkBoldNode
  | PylymarkItalicNode
  | PylymarkMarkNode;

/**
 * Parse a Pylymark string into an array of nodes.
 *
 * Supported syntax:
 *
 * - Text: plain text
 * - HanziWord: {HanziWord} (e.g. {好:good}, {好:-good} for hidden gloss)
 * - Token: [<reference> <visible text>] (e.g. [-ong Temple], [5 roof], [表 to express])
 * - Bold: **text**
 * - Italic: *text*
 * - Mark: ==text==
 */
export function parsePylymark(value: string): PylymarkNode[] {
  const nodes: PylymarkNode[] = [];

  // Regex patterns for HanziWord, Token, Bold, Italic, and Mark
  const regex =
    /\{([^:]+):(-)?([^}]+)\}|\[(.+?) (.+?)\]|\*\*(.+?)\*\*|\*(.+?)\*|==(.+?)==/gu;
  let match;
  let lastIndex = 0;

  while ((match = regex.exec(value)) !== null) {
    const [
      _fullMatch,
      hanziWordHanzi,
      hanziWordHideGloss,
      hanziWordMeaningKey,
      tokenRef,
      tokenText,
      boldText,
      italicText,
      markText,
    ] = match;
    const startIndex = match.index;
    const endIndex = regex.lastIndex;

    // Add or merge text node for the text before the match
    if (startIndex > lastIndex) {
      const text = value.slice(lastIndex, startIndex);
      const lastNode = nodes.at(-1);
      if (lastNode?.type === `text`) {
        lastNode.text += text; // Merge with the last text node
      } else {
        nodes.push({
          type: `text`,
          text,
        });
      }
    }

    if (hanziWordHanzi != null && hanziWordMeaningKey != null) {
      // Add HanziWord node for the match
      nodes.push({
        type: `hanziWord`,
        hanziWord: buildHanziWord(hanziWordHanzi, hanziWordMeaningKey),
        showGloss: hanziWordHideGloss != `-`,
      });
    } else if (tokenRef != null && tokenText != null) {
      nodes.push({
        type: `token`,
        ref: tokenRef,
        text: tokenText,
      });
    } else if (boldText != null) {
      // Add Bold node for the match
      nodes.push({
        type: `bold`,
        text: boldText,
      });
    } else if (italicText != null) {
      // Add Italic node for the match
      nodes.push({
        type: `italic`,
        text: italicText,
      });
    } else if (markText != null) {
      // Add Mark node for the match
      nodes.push({
        type: `mark`,
        text: markText,
      });
    }

    lastIndex = endIndex;
  }

  // Add or merge text node for the remaining text after the last match
  if (lastIndex < value.length) {
    const text = value.slice(lastIndex);
    const lastNode = nodes.at(-1);
    if (lastNode?.type === `text`) {
      lastNode.text += text; // Merge with the last text node
    } else {
      nodes.push({
        type: `text`,
        text,
      });
    }
  }

  return nodes;
}

export function stringifyPylymark(nodes: PylymarkNode[]): string {
  return nodes
    .map((node) => {
      switch (node.type) {
        case `text`: {
          return node.text;
        }
        case `hanziWord`: {
          return `{${hanziFromHanziWord(node.hanziWord)}:${node.showGloss ? `` : `-`}${meaningKeyFromHanziWord(node.hanziWord)}}`;
        }
        case `bold`: {
          return `**${node.text}**`;
        }
        case `italic`: {
          return `*${node.text}*`;
        }
        case `mark`: {
          return `==${node.text}==`;
        }
        case `token`: {
          return `[${node.ref} ${node.text}]`;
        }
      }
    })
    .join(``);
}

/**
 * Convert token nodes to text nodes, preserving the text content.
 */
export function stripTokens(nodes: PylymarkNode[]): PylymarkNode[] {
  return nodes.map((node) =>
    node.type === `token` ? { type: `text`, text: node.text } : node,
  );
}
